'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

export type UserRole = 'owner' | 'manager' | 'appraiser';

export interface RoleUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  avatar: string;
}

export interface RolePermissions {
  // Inventory
  canViewInventory: boolean;
  canEditInventory: boolean;
  canDeleteInventory: boolean;
  canViewPricing: boolean;
  canEditPricing: boolean;
  // Sales
  canViewSales: boolean;
  canEditSales: boolean;
  canViewSalesPricing: boolean;
  // CRM
  canViewCRM: boolean;
  canEditCRM: boolean;
  canViewCRMFinancials: boolean;
  // Purchases
  canViewPurchases: boolean;
  canEditPurchases: boolean;
  // Memos & Holds
  canViewMemos: boolean;
  canEditMemos: boolean;
  canViewHolds: boolean;
  canEditHolds: boolean;
  // Reports & Analytics
  canViewAnalytics: boolean;
  canViewActivityLog: boolean;
  canViewCompliance: boolean;
  // Settings
  canViewSettings: boolean;
  canEditSettings: boolean;
  // Certificates
  canViewCertificates: boolean;
  canViewRapaport: boolean;
  // Accounting
  canViewAccountingFull: boolean; // admin/owner: full access (all tabs + editing)
  canViewAccountingTransactions: boolean; // manager: view-only transactions tab
  canViewAccountingKPIs: boolean; // staff/appraiser: summary KPIs only
}

const ROLE_PERMISSIONS: Record<UserRole, RolePermissions> = {
  owner: {
    canViewInventory: true,
    canEditInventory: true,
    canDeleteInventory: true,
    canViewPricing: true,
    canEditPricing: true,
    canViewSales: true,
    canEditSales: true,
    canViewSalesPricing: true,
    canViewCRM: true,
    canEditCRM: true,
    canViewCRMFinancials: true,
    canViewPurchases: true,
    canEditPurchases: true,
    canViewMemos: true,
    canEditMemos: true,
    canViewHolds: true,
    canEditHolds: true,
    canViewAnalytics: true,
    canViewActivityLog: true,
    canViewCompliance: true,
    canViewSettings: true,
    canEditSettings: true,
    canViewCertificates: true,
    canViewRapaport: true,
    canViewAccountingFull: true,
    canViewAccountingTransactions: true,
    canViewAccountingKPIs: true,
  },
  manager: {
    canViewInventory: true,
    canEditInventory: true,
    canDeleteInventory: false,
    canViewPricing: true,
    canEditPricing: false,
    canViewSales: true,
    canEditSales: true,
    canViewSalesPricing: true,
    canViewCRM: true,
    canEditCRM: true,
    canViewCRMFinancials: false,
    canViewPurchases: true,
    canEditPurchases: true,
    canViewMemos: true,
    canEditMemos: true,
    canViewHolds: true,
    canEditHolds: true,
    canViewAnalytics: true,
    canViewActivityLog: true,
    canViewCompliance: false,
    canViewSettings: false,
    canEditSettings: false,
    canViewCertificates: true,
    canViewRapaport: true,
    canViewAccountingFull: false,
    canViewAccountingTransactions: true,
    canViewAccountingKPIs: true,
  },
  appraiser: {
    canViewInventory: true,
    canEditInventory: false,
    canDeleteInventory: false,
    canViewPricing: false,
    canEditPricing: false,
    canViewSales: false,
    canEditSales: false,
    canViewSalesPricing: false,
    canViewCRM: false,
    canEditCRM: false,
    canViewCRMFinancials: false,
    canViewPurchases: false,
    canEditPurchases: false,
    canViewMemos: true,
    canEditMemos: false,
    canViewHolds: true,
    canEditHolds: false,
    canViewAnalytics: false,
    canViewActivityLog: false,
    canViewCompliance: false,
    canViewSettings: false,
    canEditSettings: false,
    canViewCertificates: true,
    canViewRapaport: true,
    canViewAccountingFull: false,
    canViewAccountingTransactions: false,
    canViewAccountingKPIs: true,
  },
};

// Demo credentials
export const DEMO_USERS: RoleUser[] = [];

export const DEMO_PASSWORDS: Record<string, string> = {};

const ROLE_STORAGE_KEY = 'glanz_role_user';
const LEGACY_ROLE_STORAGE_KEY = 'diamond_crm_role_user';

/** Read the stored role session, migrating the pre-rebrand key once if found. */
function readStoredRoleUser(): string | null {
  const current = localStorage.getItem(ROLE_STORAGE_KEY);
  if (current) return current;
  const legacy = localStorage.getItem(LEGACY_ROLE_STORAGE_KEY);
  if (legacy) {
    localStorage.setItem(ROLE_STORAGE_KEY, legacy);
    localStorage.removeItem(LEGACY_ROLE_STORAGE_KEY);
  }
  return legacy;
}

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function normalizeRole(raw: unknown): UserRole {
  const value = String(raw || '').toLowerCase();
  if (value === 'manager' || value === 'appraiser' || value === 'owner') return value;
  return 'owner';
}

/** Map a Supabase auth user into the RoleUser shape used by AppLayout / permissions. */
export function roleUserFromAuthUser(authUser: {
  id: string;
  email?: string | null;
  user_metadata?: Record<string, unknown> | null;
}): RoleUser {
  const meta = authUser.user_metadata || {};
  const email = (authUser.email || '').toLowerCase();
  const name =
    (typeof meta.full_name === 'string' && meta.full_name) ||
    (typeof meta.name === 'string' && meta.name) ||
    email.split('@')[0] ||
    'User';
  return {
    id: authUser.id,
    name,
    email,
    role: normalizeRole(meta.role),
    avatar: initialsFromName(name),
  };
}

interface RoleAuthContextType {
  roleUser: RoleUser | null;
  permissions: RolePermissions | null;
  roleLoading: boolean;
  roleLogin: (email: string, password: string, roleHint?: UserRole) => Promise<void>;
  roleLogout: () => Promise<void>;
  setRoleFromAuthUser: (authUser: {
    id: string;
    email?: string | null;
    user_metadata?: Record<string, unknown> | null;
  }) => void;
  hasPermission: (key: keyof RolePermissions) => boolean;
}

const RoleAuthContext = createContext<RoleAuthContextType>({
  roleUser: null,
  permissions: null,
  roleLoading: true,
  roleLogin: async () => {},
  roleLogout: async () => {},
  setRoleFromAuthUser: () => {},
  hasPermission: () => false,
});

export const useRoleAuth = () => useContext(RoleAuthContext);

export const RoleAuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [roleUser, setRoleUser] = useState<RoleUser | null>(null);
  const [roleLoading, setRoleLoading] = useState(true);

  const persistRoleUser = useCallback((user: RoleUser | null) => {
    setRoleUser(user);
    try {
      if (user) {
        localStorage.setItem(ROLE_STORAGE_KEY, JSON.stringify(user));
      } else {
        localStorage.removeItem(ROLE_STORAGE_KEY);
      }
    } catch {
      // ignore storage errors
    }
  }, []);

  const setRoleFromAuthUser = useCallback(
    (authUser: {
      id: string;
      email?: string | null;
      user_metadata?: Record<string, unknown> | null;
    }) => {
      persistRoleUser(roleUserFromAuthUser(authUser));
    },
    [persistRoleUser]
  );

  useEffect(() => {
    let cancelled = false;
    const supabase = createClient();

    const bootstrap = async () => {
      try {
        // Prefer an active Supabase session so real auth users can access the app.
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (cancelled) return;

        if (session?.user) {
          persistRoleUser(roleUserFromAuthUser(session.user));
          setRoleLoading(false);
          return;
        }

        // Fall back to demo / local role session
        const stored = readStoredRoleUser();
        if (stored) {
          persistRoleUser(JSON.parse(stored));
        }
      } catch {
        try {
          const stored = readStoredRoleUser();
          if (stored && !cancelled) {
            persistRoleUser(JSON.parse(stored));
          }
        } catch {
          // ignore
        }
      } finally {
        if (!cancelled) setRoleLoading(false);
      }
    };

    bootstrap();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        // Clear Supabase-backed role sessions; keep hardcoded demo users if still stored
        try {
          const stored = readStoredRoleUser();
          if (stored) {
            const parsed = JSON.parse(stored) as RoleUser;
            const isDemo = DEMO_USERS.some((u) => u.id === parsed?.id);
            if (!isDemo) persistRoleUser(null);
          }
        } catch {
          persistRoleUser(null);
        }
        return;
      }

      if (session?.user) {
        persistRoleUser(roleUserFromAuthUser(session.user));
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [persistRoleUser]);

  const roleLogin = async (email: string, password: string, roleHint?: UserRole) => {
    const supabase = createClient();
    const normalizedEmail = email.toLowerCase().trim();
    const normalizedRole = roleHint ? (roleHint.toLowerCase().trim() as UserRole) : undefined;

    // First try real Supabase auth
    const { data, error } = await supabase.auth.signInWithPassword({
      email: normalizedEmail,
      password,
    });

    if (!error && data.user) {
      // Real Supabase auth succeeded
      const baseUser = roleUserFromAuthUser(data.user);

      if (normalizedRole) {
        const storedRole = ((data.user.user_metadata?.role as string) || '').toLowerCase().trim();
        if (storedRole && storedRole !== normalizedRole) {
          await supabase.auth.signOut();
          throw new Error('Invalid email, password, or role');
        }
      }

      const finalUser: RoleUser = normalizedRole ? { ...baseUser, role: normalizedRole } : baseUser;
      persistRoleUser(finalUser);
      return;
    }

    // Supabase auth failed — try demo_registrations table (demo users)
    const { data: demoRows, error: demoError } = await supabase
      .from('demo_registrations')
      .select('id, full_name, email, role, password_hash, status, demo_expires_at, suspended_at')
      .eq('email', normalizedEmail)
      .in('status', ['demo_active', 'active', 'Active'])
      .limit(1);

    if (demoError || !demoRows || demoRows.length === 0) {
      throw new Error('Invalid email, password, or role');
    }

    const demoUser = demoRows[0];

    // Check if demo is suspended
    if (demoUser.suspended_at) {
      throw new Error('Invalid email, password, or role');
    }

    // Check if demo has expired
    if (demoUser.demo_expires_at && new Date(demoUser.demo_expires_at) < new Date()) {
      throw new Error('Invalid email, password, or role');
    }

    // Verify password hash: format is "sha256:<salt>:<hash>"
    const storedHash = demoUser.password_hash as string | null;
    if (!storedHash) {
      throw new Error('Invalid email, password, or role');
    }

    const parts = storedHash.split(':');
    if (parts.length !== 3 || parts[0] !== 'sha256') {
      throw new Error('Invalid email, password, or role');
    }

    const [, salt, expectedHash] = parts;
    const encoder = new TextEncoder();
    const data2 = encoder.encode(salt + password);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data2);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const computedHash = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

    if (computedHash !== expectedHash) {
      throw new Error('Invalid email, password, or role');
    }

    // Validate role if a hint was provided
    const demoRole = ((demoUser.role as string) || '').toLowerCase().trim() as UserRole;
    if (normalizedRole && demoRole && demoRole !== normalizedRole) {
      throw new Error('Invalid email, password, or role');
    }

    const resolvedRole: UserRole = normalizedRole || demoRole || 'owner';

    const name = (demoUser.full_name as string) || normalizedEmail.split('@')[0] || 'Demo User';
    const demoRoleUser: RoleUser = {
      id: `demo_${demoUser.id}`,
      name,
      email: normalizedEmail,
      role: resolvedRole,
      avatar: initialsFromName(name),
    };

    persistRoleUser(demoRoleUser);
  };

  const roleLogout = async () => {
    const supabase = createClient();
    persistRoleUser(null);
    try {
      await supabase.auth.signOut();
    } catch {
      // ignore sign-out errors
    }
  };

  const permissions = roleUser ? ROLE_PERMISSIONS[roleUser.role] : null;

  const hasPermission = (key: keyof RolePermissions): boolean => {
    if (!permissions) return false;
    return permissions[key];
  };

  return (
    <RoleAuthContext.Provider
      value={{
        roleUser,
        permissions,
        roleLoading,
        roleLogin,
        roleLogout,
        setRoleFromAuthUser,
        hasPermission,
      }}
    >
      {children}
    </RoleAuthContext.Provider>
  );
};
